import React, { useState } from 'react';
import { Box, TextField } from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';

interface NumberFieldRendererProps {
  value?: string;
  addToActionItems?: boolean;
  onChange: (value: string, addToActionItems: boolean) => void;
  fieldName: string;
  label?: string;
  showBookmark?: boolean;
  disabled?: boolean;
  valueColor?: string;
  /** Called on blur to format the displayed value (e.g. bracket negatives). Raw value is still stored. */
  formatOnBlur?: (value: string) => string;
  /** Hide the browser number-input spin buttons */
  hideSpinners?: boolean;
}

export const NumberFieldRenderer: React.FC<NumberFieldRendererProps> = ({
  value = '',
  addToActionItems = false,
  onChange,
  fieldName,
  label,
  showBookmark = true,
  disabled = false,
  valueColor,
  formatOnBlur,
  hideSpinners = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const displayValue = !isFocused && formatOnBlur ? formatOnBlur(value) : value;

  const spinnerSx = hideSpinners
    ? {
        '& input[type=number]::-webkit-outer-spin-button': { display: 'none' },
        '& input[type=number]::-webkit-inner-spin-button': { display: 'none' },
        '& input[type=number]': { MozAppearance: 'textfield' },
      }
    : undefined;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <TextField
        fullWidth
        type={disabled || (!isFocused && formatOnBlur) ? 'text' : 'number'}
        label={label}
        value={displayValue}
        onChange={e => onChange(e.target.value, addToActionItems)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        InputLabelProps={{ shrink: true }}
        size='small'
        disabled={disabled}
        sx={{
          ...(disabled && valueColor
            ? {
                '& .MuiInputBase-input.Mui-disabled': {
                  color: valueColor,
                  fontWeight: 600,
                  WebkitTextFillColor: valueColor,
                },
              }
            : undefined),
          ...spinnerSx,
        }}
      />
      {showBookmark ? (
        <Box sx={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => onChange(value, !addToActionItems)}>
          {addToActionItems ? <BookmarkIcon sx={{ color: 'primary.main' }} /> : <BookmarkBorderIcon sx={{ color: 'action.disabled' }} />}
        </Box>
      ) : (
        <Box sx={{ width: 24, flexShrink: 0 }} />
      )}
    </Box>
  );
};
